import { useState, useEffect } from "react";
import { format, subDays, subMonths, startOfMonth, endOfMonth, startOfYear, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Calendar as CalendarIcon, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { DateRange } from "react-day-picker";

interface DateRangePickerProps {
  startDate: string;
  endDate: string;
  onDateRangeChange: (startDate: string, endDate: string) => void;
}

type PresetKey = 'today' | 'last7days' | 'last30days' | 'last90days' | 'thisMonth' | 'lastMonth' | 'thisYear';

const presets: { key: PresetKey; label: string }[] = [
  { key: 'today', label: 'Hoje' },
  { key: 'last7days', label: 'Últimos 7 dias' },
  { key: 'last30days', label: 'Últimos 30 dias' },
  { key: 'last90days', label: 'Últimos 90 dias' },
  { key: 'thisMonth', label: 'Este mês' },
  { key: 'lastMonth', label: 'Mês passado' },
  { key: 'thisYear', label: 'Este ano' },
];

export function DateRangePicker({ startDate, endDate, onDateRangeChange }: DateRangePickerProps) {
  const [open, setOpen] = useState(false);
  const [selectedRange, setSelectedRange] = useState<DateRange | undefined>({
    from: parseISO(startDate),
    to: parseISO(endDate)
  });
  const [activePreset, setActivePreset] = useState<PresetKey | null>('last30days');

  // Sincronizar estado interno com props externas
  useEffect(() => {
    setSelectedRange({
      from: parseISO(startDate),
      to: parseISO(endDate)
    });
  }, [startDate, endDate]);

  const getPresetDates = (preset: PresetKey): { start: Date; end: Date } => {
    const today = new Date();
    let start: Date;
    let end: Date = today;

    switch (preset) {
      case 'today':
        start = today;
        break;
      case 'last7days':
        start = subDays(today, 7);
        break;
      case 'last30days':
        start = subDays(today, 30);
        break;
      case 'last90days':
        start = subDays(today, 90);
        break;
      case 'thisMonth':
        start = startOfMonth(today);
        break;
      case 'lastMonth':
        const lastMonth = subMonths(today, 1);
        start = startOfMonth(lastMonth);
        end = endOfMonth(lastMonth);
        break;
      case 'thisYear':
        start = startOfYear(today);
        break;
      default:
        start = subDays(today, 30);
    }

    return { start, end };
  };

  const applyPreset = (preset: PresetKey) => {
    const { start, end } = getPresetDates(preset);
    setSelectedRange({ from: start, to: end });
    setActivePreset(preset);
    onDateRangeChange(
      format(start, 'yyyy-MM-dd'),
      format(end, 'yyyy-MM-dd')
    );
    setOpen(false);
  };

  const handleRangeSelect = (range: DateRange | undefined) => {
    setSelectedRange(range);
    setActivePreset(null); // Limpa preset ativo quando seleciona manualmente
  };

  const handleApply = () => {
    if (selectedRange?.from && selectedRange?.to) {
      onDateRangeChange(
        format(selectedRange.from, 'yyyy-MM-dd'),
        format(selectedRange.to, 'yyyy-MM-dd')
      );
      setOpen(false);
    }
  };

  const formatDisplayDate = () => {
    if (activePreset) {
      const preset = presets.find(p => p.key === activePreset);
      if (preset) return preset.label;
    }
    return `${format(parseISO(startDate), 'dd/MM/yyyy')} - ${format(parseISO(endDate), 'dd/MM/yyyy')}`;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="min-w-[200px] justify-start text-left font-normal">
          <CalendarIcon className="mr-2 h-4 w-4" />
          <span className="truncate">{formatDisplayDate()}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="end">
        <div className="flex flex-col sm:flex-row">
          {/* Presets */}
          <div className="border-b sm:border-b-0 sm:border-r p-3 space-y-1 min-w-[160px]">
            <p className="text-xs font-medium text-muted-foreground mb-2 px-2">Atalhos</p>
            {presets.map((preset) => (
              <Button
                key={preset.key}
                variant="ghost"
                size="sm"
                className={cn(
                  "w-full justify-between",
                  activePreset === preset.key && "bg-accent"
                )}
                onClick={() => applyPreset(preset.key)}
              >
                {preset.label}
                {activePreset === preset.key && (
                  <Check className="h-4 w-4 text-primary" />
                )}
              </Button>
            ))}
          </div>
          
          {/* Calendário */}
          <div className="p-3 space-y-3">
            <p className="text-xs font-medium text-muted-foreground px-1">
              Ou selecione um período personalizado
            </p>
            <Calendar
              mode="range"
              selected={selectedRange}
              onSelect={handleRangeSelect}
              numberOfMonths={1}
              disabled={(date) => date > new Date()}
              locale={ptBR}
              className={cn("pointer-events-auto rounded-md border")}
            />
            
            {/* Período selecionado */}
            <div className="flex items-center justify-between px-1 py-2 bg-muted/50 rounded-md">
              <div className="text-sm">
                <span className="text-muted-foreground">De: </span>
                <span className="font-medium">
                  {selectedRange?.from ? format(selectedRange.from, 'dd/MM/yyyy') : '—'}
                </span>
              </div>
              <div className="text-sm">
                <span className="text-muted-foreground">Até: </span>
                <span className="font-medium">
                  {selectedRange?.to ? format(selectedRange.to, 'dd/MM/yyyy') : '—'}
                </span>
              </div>
            </div>
            
            <Button 
              onClick={handleApply} 
              className="w-full"
              disabled={!selectedRange?.from || !selectedRange?.to}
            >
              Aplicar Período
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
